import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { capacidades } from "@/lib/capacidades";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarClock } from "lucide-react";
import { slotTexto, avisoTexto, ESTADO_OCURRENCIA } from "@/lib/programados";

// v2.13.0 — LISTA OPERATIVA de Programados, por CARRIL autorizado. El carril lo decide el backend
// (institucional para actores institucionales); Programados nunca entra a la cola de Central.
export default function Programados() {
  const { user } = useAuth();
  const caps = capacidades(user);
  const [data, setData] = useState(null);
  const [carril, setCarril] = useState("residencial_galarya");
  const [estado, setEstado] = useState("accionables");
  const [resolver, setResolver] = useState(null); // {ocurrencia, destino}
  const [nota, setNota] = useState("");
  const [motivo, setMotivo] = useState("");

  const load = useCallback(() => {
    api.get(`/programados/ocurrencias?estado=${estado}&carril=${carril}&limite=300`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("No se pudieron cargar los programados."));
  }, [estado, carril]);

  useEffect(() => { load(); }, [load]);

  const registrar = async () => {
    const { ocurrencia, destino } = resolver;
    try {
      await api.post(`/programados/ocurrencias/${ocurrencia.id}/${destino}`, { nota, motivo });
      toast.success(destino === "completar" ? "Registrado como completado." : "Registrado como no realizado.");
      setResolver(null); setNota(""); setMotivo("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo registrar.");
    }
  };

  const ocs = data?.ocurrencias || [];

  return (
    // Mismo defecto que Organizaciones: sin la marca del puente de tema
    // (index.css · `.tema-noche-auto`), los dos `SelectTrigger` (`bg-white`), el campo de carril
    // (`bg-slate-50`) y las tarjetas de la lista quedaban CLAROS con el texto ya aclarado a
    // near-white. Sólo presentación: valores, filtros, consultas y testids intactos.
    <div className="tema-noche-auto space-y-6" data-testid="programados-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#012472] flex items-center gap-2">
            <CalendarClock strokeWidth={1.6} className="h-6 w-6" /> Programados
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Vista de SUPERVISIÓN de los recordatorios programados, en la hora local del sitio.
            La operación real ocurre en Central: al llegar la hora, el recordatorio genera una
            alerta media normal que se toma y se cierra ahí.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {caps.interno && (
            <Select value={carril} onValueChange={setCarril}>
              <SelectTrigger className="rounded-xl bg-white w-[220px]" aria-label="Filtrar por carril"
                data-testid="programados-filter-carril"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="residencial_galarya" data-testid="programados-carril-residencial">Residencial Galarya</SelectItem>
                <SelectItem value="institucional" data-testid="programados-carril-institucional">Institucional</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="rounded-xl bg-white w-[200px]" aria-label="Filtrar por estado"
              data-testid="programados-filter-estado"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="accionables" data-testid="programados-estado-accionables">Pendientes y vencidos</SelectItem>
              <SelectItem value="pendiente" data-testid="programados-estado-pendiente">Pendientes</SelectItem>
              <SelectItem value="vencida" data-testid="programados-estado-vencida">Vencidos</SelectItem>
              <SelectItem value="completada" data-testid="programados-estado-completada">Completados</SelectItem>
              <SelectItem value="no_realizada" data-testid="programados-estado-no-realizada">No realizados</SelectItem>
            </SelectContent>
          </Select>
          <Input readOnly value={data?.carril || ""} aria-label="Carril autorizado"
            className="rounded-xl bg-slate-50 w-[200px] text-xs" data-testid="programados-carril-activo" />
        </div>
      </div>

      {ocs.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="programados-vacio">Nada que atender en este carril.</p>
      ) : (
        <div className="space-y-2" data-testid="programados-lista">
          {ocs.map((o) => (
            <div key={o.id} className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-wrap items-center justify-between gap-3"
              data-testid={`programados-item-${o.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-slate-600">{slotTexto(o.slot_local)}</span>
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${(ESTADO_OCURRENCIA[o.estado] || {}).tono || ""}`}
                    data-testid={`programados-estado-${o.id}`}>
                    {(ESTADO_OCURRENCIA[o.estado] || {}).label || o.estado}
                  </span>
                  {o.entregada_tarde && (
                    <span className="text-xs text-amber-600" data-testid={`programados-tarde-${o.id}`}>
                      entregada tarde · {o.retraso_min} min
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium text-slate-800 mt-1 truncate">{o.tarea?.titulo || "(programado)"}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {o.persona_nombre || "(persona)"} · hora objetivo {String(o.slot_local || "").split("T")[1]} · {avisoTexto(o.anticipacion_min)}
                </p>
                {o.tarea?.instruccion && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{o.tarea.instruccion}</p>}
              </div>
              {caps.operarProgramados && ["pendiente", "vencida"].includes(o.estado) && (
                <div className="flex items-center gap-2">
                  <Button size="sm" className="rounded-lg bg-[#012472] hover:bg-[#012472]/90 text-white"
                    onClick={() => setResolver({ ocurrencia: o, destino: "completar" })}
                    data-testid={`programados-completar-${o.id}`}>Completar</Button>
                  <Button size="sm" variant="outline" className="rounded-lg"
                    onClick={() => setResolver({ ocurrencia: o, destino: "no-realizada" })}
                    data-testid={`programados-no-realizada-${o.id}`}>No realizada</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!resolver} onOpenChange={(v) => { if (!v) { setResolver(null); setNota(""); setMotivo(""); } }}>
        <DialogContent data-testid="programados-resultado-dialog">
          <DialogHeader>
            <DialogTitle>
              {resolver?.destino === "completar" ? "Registrar como completado" : "Registrar como no realizado"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {resolver?.destino === "no-realizada" && (
              <Input value={motivo} maxLength={300} onChange={(e) => setMotivo(e.target.value)}
                placeholder="Motivo" className="rounded-xl" data-testid="programados-resultado-motivo" />
            )}
            <Input value={nota} maxLength={300} onChange={(e) => setNota(e.target.value)}
              placeholder="Nota del resultado (opcional, máx. 300)" className="rounded-xl"
              data-testid="programados-resultado-nota" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setResolver(null)}
              data-testid="programados-resultado-cancelar">Cancelar</Button>
            <Button className="rounded-xl bg-[#012472] hover:bg-[#012472]/90 text-white"
              onClick={registrar} data-testid="programados-resultado-guardar">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
